/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  IHeaderComp,
  IHeaderParams,
  LongTapEvent,
  TouchListener,
} from 'ag-grid-community';

import { GRID_EVENT } from '../../grid-public-events';

export interface HeaderWithExternalFilterCallbackParams {
  setFiltered(filtered: boolean): void;
}

export interface HeaderWithExternalFilterParams extends IHeaderParams {
  title?: string;

  onInit?(params: HeaderWithExternalFilterCallbackParams): void;

  onDestroy?(): void;

  onFilter?(params: HeaderWithExternalFilterCallbackParams): void;
}

export class HeaderWithExternalFilterComponent implements IHeaderComp {
  private static TEMPLATE_HTML =
    '<div class="ag-cell-label-container" role="presentation">' +
    '  <span class="ag-header-icon grid-header-cell-external-filter" data-ref="eExternalFilter" aria-hidden="true"></span>' +
    '  <div class="ag-header-cell-label" data-ref="eLabel" role="presentation">' +
    '    <span class="ag-header-cell-text" data-ref="eText" role="columnheader"></span>' +
    '    <span class="ag-header-icon ag-sort-order" data-ref="eSortOrder" aria-hidden="true"></span>' +
    '    <span class="ag-sort-indicator-container" role="presentation">' +
    '      <span class="ag-header-icon ag-sort-indicator-icon ag-sort-ascending-icon" data-ref="eSortAsc" aria-hidden="true"></span>' +
    '      <span class="ag-header-icon ag-sort-indicator-icon ag-sort-descending-icon" data-ref="eSortDesc" aria-hidden="true"></span>' +
    '      <span class="ag-header-icon ag-sort-indicator-icon ag-sort-none-icon" data-ref="eSortNone" aria-hidden="true"></span>' +
    '    </span>' +
    '  </div>' +
    '</div>';

  private params: HeaderWithExternalFilterParams;
  private eGui: HTMLElement;
  private eSortAsc: HTMLElement;
  private eSortDesc: HTMLElement;
  private eSortNone: HTMLElement;
  private eSortOrder: HTMLElement;
  private eExternalFilter: HTMLElement;
  private eLabel: HTMLElement;
  private eText: HTMLElement;

  private readonly cleanup: Array<() => void> = [];

  init(params: HeaderWithExternalFilterParams): void {
    this.params = params;
    const wrap = document.createElement('div');
    wrap.innerHTML = HeaderWithExternalFilterComponent.TEMPLATE_HTML;
    this.eGui = wrap.firstElementChild as HTMLElement;

    this.eExternalFilter = this.eGui.querySelector('[data-ref="eExternalFilter"]') as HTMLElement;
    this.eLabel = this.eGui.querySelector('[data-ref="eLabel"]') as HTMLElement;
    this.eText = this.eGui.querySelector('[data-ref="eText"]') as HTMLElement;
    this.eSortOrder = this.eGui.querySelector('[data-ref="eSortOrder"]') as HTMLElement;
    this.eSortAsc = this.eGui.querySelector('[data-ref="eSortAsc"]') as HTMLElement;
    this.eSortDesc = this.eGui.querySelector('[data-ref="eSortDesc"]') as HTMLElement;
    this.eSortNone = this.eGui.querySelector('[data-ref="eSortNone"]') as HTMLElement;

    this.setupTap();
    this.setupExternalFilter();
    this.setupSort();
    this.setupText(params.displayName);

    if (this.params.onInit) {
      this.params.onInit(this.createCallbackParams());
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  destroy(): void {
    if (this.params.onDestroy) {
      this.params.onDestroy();
    }
    this.cleanup.forEach((fn) => fn());
    this.cleanup.length = 0;
  }

  refresh(params: IHeaderParams): boolean {
    this.params = params as HeaderWithExternalFilterParams;
    this.setupText(params.displayName);
    this.onSortChanged();
    this.setMultiSortOrder();
    return true;
  }

  showExternalFilter(_eventSource: HTMLElement): void {
    if (this.params.onFilter) {
      this.params.onFilter(this.createCallbackParams());
    }
  }

  private setupSort(): void {
    const enableSorting = this.params.enableSorting;

    if (!enableSorting) {
      this.removeSortIcons();
      return;
    }

    const sortUsingCtrl = this.params.api.getGridOption('multiSortKey') === 'ctrl';

    if (this.eLabel) {
      const onClick = (event: MouseEvent) => {
        const multiSort = sortUsingCtrl ? (event.ctrlKey || event.metaKey) : event.shiftKey;
        this.params.progressSort(multiSort);
      };
      this.eLabel.addEventListener('click', onClick);
      this.cleanup.push(() => this.eLabel.removeEventListener('click', onClick));
    }

    const onColSort = () => this.onSortChanged();
    this.params.column.addEventListener('sortChanged', onColSort);
    this.cleanup.push(() => this.params.column.removeEventListener('sortChanged', onColSort));
    this.onSortChanged();

    const onGridSort = () => this.setMultiSortOrder();
    this.params.api.addEventListener(GRID_EVENT.sortChanged, onGridSort);
    this.cleanup.push(() => this.params.api.removeEventListener(GRID_EVENT.sortChanged, onGridSort));
    this.setMultiSortOrder();
  }

  private setupText(displayName: string): void {
    if (this.eText) {
      this.eText.innerHTML = displayName;
    }
  }

  private setupTap(): void {
    if (this.params.api.getGridOption('suppressTouch')) {
      return;
    }

    const touchListener = new TouchListener(this.getGui());

    if (this.params.enableMenu) {
      const longTapListener = (event: LongTapEvent) => {
        this.params.showColumnMenuAfterMouseClick(event.touchStart);
      };
      touchListener.addEventListener('longTap', longTapListener);
      this.cleanup.push(() => touchListener.removeEventListener('longTap', longTapListener));
    }

    if (this.params.enableSorting) {
      const tapListener = () => {
        this.params.progressSort(false);
      };
      touchListener.addEventListener('tap', tapListener);
      this.cleanup.push(() => touchListener.removeEventListener('tap', tapListener));
    }

    this.cleanup.push(() => touchListener.destroy());
  }

  private setupExternalFilter(): void {
    if (!this.eExternalFilter) {
      return;
    }

    const onClick = () => this.showExternalFilter(this.eExternalFilter);
    this.eExternalFilter.addEventListener('click', onClick);
    this.cleanup.push(() => this.eExternalFilter.removeEventListener('click', onClick));
    if (this.params.title) {
      this.eExternalFilter.title = this.params.title;
    }
  }

  private removeSortIcons(): void {
    this.eSortAsc?.remove();
    this.eSortDesc?.remove();
    this.eSortNone?.remove();
    this.eSortOrder?.remove();
  }

  private onSortChanged(): void {
    const col = this.params.column;
    const gui = this.getGui();
    gui.classList.toggle('ag-header-cell-sorted-asc', col.getSort() === 'asc');
    gui.classList.toggle('ag-header-cell-sorted-desc', col.getSort() === 'desc');
    gui.classList.toggle('ag-header-cell-sorted-none', col.getSort() == null);

    if (this.eSortAsc) {
      this.eSortAsc.classList.toggle('ag-hidden', col.getSort() !== 'asc');
    }

    if (this.eSortDesc) {
      this.eSortDesc.classList.toggle('ag-hidden', col.getSort() !== 'desc');
    }

    if (this.eSortNone) {
      const alwaysHideNoSort = !this.params.column.getColDef().unSortIcon && !this.params.api.getGridOption('unSortIcon');
      this.eSortNone.classList.toggle(
        'ag-hidden',
        alwaysHideNoSort || col.getSort() != null,
      );
    }
  }

  private setMultiSortOrder(): void {
    if (!this.eSortOrder) {
      return;
    }

    const col = this.params.column;
    const sortIndex = col.getSortIndex();
    const hasSort = col.getSort() != null;
    const multi = this.params.api.getColumnState().filter((c) => c.sort != null).length > 1;
    const showIndex = hasSort && multi;

    this.eSortOrder.classList.toggle('ag-hidden', !showIndex);
    this.eSortOrder.innerHTML =
      showIndex && sortIndex != null && sortIndex >= 0 ? String(sortIndex + 1) : '';
  }

  private createCallbackParams(): HeaderWithExternalFilterCallbackParams {
    return { setFiltered: this.setFiltered.bind(this) };
  }

  private setFiltered(filtered: boolean): void {
    this.eExternalFilter.classList.toggle('grid-header-cell-external-filter-filtered', filtered);
  }
}
